import { grpcClientFactory } from './grpcClientFactory';
import '../../_proto/landscope-map-viewer_pb';
const readLayerValues = (sample) => {
    const out = {};
    const map = typeof sample.getLayerValuesMap === 'function' ? sample.getLayerValuesMap() : null;
    if (!map)
        return out;
    // jspb.Map exposes forEach((value, key) => ...)
    map.forEach((v, k) => {
        out[k] = { value: v.getValue(), valid: v.getValid() };
    });
    return out;
};
const readHemisphere = (response) => {
    const h = typeof response.getHemisphere === 'function' ? response.getHemisphere() : '';
    return h === 'S' ? 'S' : 'N';
};
const readLayerStats = (response) => {
    const out = {};
    const map = typeof response.getLayerStatsMap === 'function' ? response.getLayerStatsMap() : null;
    if (!map)
        return out;
    map.forEach((v, k) => {
        out[k] = { min: v.getMin(), max: v.getMax() };
    });
    return out;
};
const SAMPLE_SPACING_METERS = 1;
const MIN_SAMPLES = 50;
const MAX_SAMPLES = 5000;
// The analytical layers the elevation chart can colour-by on top of elevation.
// Threaded through every path/measurement profile fetch so the response always
// carries per-sample values + raster-wide layer_stats — chart colour scales
// stay aligned with the on-map raster regardless of which path is being
// inspected.
export const DEFAULT_PROFILE_SAMPLING_LAYERS = ['slope', 'aspect', 'geomorphons', 'twi', 'flow_accumulation'];
export const metersToSampleCount = (distanceMeters) => {
    const target = Math.round(distanceMeters / SAMPLE_SPACING_METERS);
    return Math.max(MIN_SAMPLES, Math.min(MAX_SAMPLES, target));
};
export const elevationService = {
    async sampleElevation(projectId, lat, lng) {
        const client = grpcClientFactory.createElevationClient({ withConditionalAuth: true });
        const SampleElevationRequest = proto.landscope.mapviewer.v1.SampleElevationRequest;
        const request = new SampleElevationRequest();
        request.setProjectId(projectId);
        request.setLatitude(lat);
        request.setLongitude(lng);
        return new Promise((resolve, reject) => {
            client.sampleElevation(request, {}, (err, response) => {
                if (err) {
                    reject(err);
                    return;
                }
                if (response && response.getSuccess()) {
                    resolve(response.getElevationMeters());
                }
                else {
                    resolve(null);
                }
            });
        });
    },
    async getLineElevationProfile(projectId, startLat, startLng, endLat, endLng, numSamples = 30, samplingLayers) {
        const client = grpcClientFactory.createElevationClient({ withConditionalAuth: true });
        const GetLineElevationProfileRequest = proto.landscope.mapviewer.v1.GetLineElevationProfileRequest;
        const request = new GetLineElevationProfileRequest();
        request.setProjectId(projectId);
        request.setStartLatitude(startLat);
        request.setStartLongitude(startLng);
        request.setEndLatitude(endLat);
        request.setEndLongitude(endLng);
        request.setNumSamples(numSamples);
        if (samplingLayers && samplingLayers.length) {
            request.setSamplingLayersList(samplingLayers);
        }
        return new Promise((resolve, reject) => {
            client.getLineElevationProfile(request, {}, (err, response) => {
                if (err) {
                    reject(err);
                    return;
                }
                if (response && response.getSuccess()) {
                    const samplesList = response.getSamplesList();
                    const samples = samplesList.map((sample) => ({
                        distanceMeters: sample.getDistanceMeters(),
                        elevationMeters: sample.getElevationMeters(),
                        valid: sample.getValid(),
                        distance3dMeters: sample.getDistance3dMeters(),
                        layerValues: readLayerValues(sample)
                    }));
                    resolve({
                        samples,
                        totalDistanceMeters: response.getTotalDistanceMeters(),
                        totalDistance3dMeters: response.getTotalDistance3dMeters(),
                        hemisphere: readHemisphere(response),
                        layerStats: readLayerStats(response)
                    });
                }
                else {
                    resolve(null);
                }
            });
        });
    },
    async getPolylineElevationProfile(projectId, coordinates, numSamples = 50, samplingLayers) {
        const client = grpcClientFactory.createElevationClient({ withConditionalAuth: true });
        const GetPolylineElevationProfileRequest = proto.landscope.mapviewer.v1.GetPolylineElevationProfileRequest;
        const Coordinate = proto.landscope.mapviewer.v1.Coordinate;
        const request = new GetPolylineElevationProfileRequest();
        request.setProjectId(projectId);
        request.setNumSamples(numSamples);
        if (samplingLayers && samplingLayers.length) {
            request.setSamplingLayersList(samplingLayers);
        }
        const protoCoords = coordinates.map(coord => {
            const protoCoord = new Coordinate();
            protoCoord.setLatitude(coord.lat);
            protoCoord.setLongitude(coord.lng);
            return protoCoord;
        });
        request.setCoordinatesList(protoCoords);
        return new Promise((resolve, reject) => {
            client.getPolylineElevationProfile(request, {}, (err, response) => {
                if (err) {
                    reject(err);
                    return;
                }
                if (response && response.getSuccess()) {
                    const samplesList = response.getSamplesList();
                    const samples = samplesList.map((sample) => ({
                        distanceMeters: sample.getDistanceMeters(),
                        elevationMeters: sample.getElevationMeters(),
                        valid: sample.getValid(),
                        distance3dMeters: sample.getDistance3dMeters(),
                        layerValues: readLayerValues(sample)
                    }));
                    resolve({
                        samples,
                        totalDistanceMeters: response.getTotalDistanceMeters(),
                        totalDistance3dMeters: response.getTotalDistance3dMeters(),
                        hemisphere: readHemisphere(response),
                        layerStats: readLayerStats(response)
                    });
                }
                else {
                    resolve(null);
                }
            });
        });
    },
    async calculateStreamBuffer(projectId, lineCoordinates, bufferDistanceMeters) {
        const client = grpcClientFactory.createElevationClient({ withConditionalAuth: true });
        const CalculateStreamBufferRequest = proto.landscope.mapviewer.v1.CalculateStreamBufferRequest;
        const Coordinate = proto.landscope.mapviewer.v1.Coordinate;
        const request = new CalculateStreamBufferRequest();
        request.setProjectId(projectId);
        request.setBufferDistanceMeters(bufferDistanceMeters);
        // Convert coordinates to proto format
        const protoCoords = lineCoordinates.map(coord => {
            const protoCoord = new Coordinate();
            protoCoord.setLatitude(coord.lat);
            protoCoord.setLongitude(coord.lng);
            return protoCoord;
        });
        request.setLineCoordinatesList(protoCoords);
        return new Promise((resolve, reject) => {
            client.calculateStreamBuffer(request, {}, (err, response) => {
                if (err) {
                    reject(err);
                    return;
                }
                if (response && response.getSuccess()) {
                    const readCoord = (c) => ({
                        lat: c.getLatitude(),
                        lng: c.getLongitude(),
                    });
                    const polygonsList = response.getBufferPolygonsList();
                    let bufferPolygons;
                    if (polygonsList && polygonsList.length > 0) {
                        bufferPolygons = polygonsList.map((p) => ({
                            exterior: p.getExteriorList().map(readCoord),
                            holes: p.getHolesList().map((ring) => ring.getPointsList().map(readCoord)),
                        }));
                    }
                    else {
                        // Legacy single-ring fallback for older server responses.
                        const legacyExterior = response.getBufferPolygonList().map(readCoord);
                        bufferPolygons = legacyExterior.length >= 3
                            ? [{ exterior: legacyExterior, holes: [] }]
                            : [];
                    }
                    resolve({
                        success: true,
                        bufferPolygons,
                    });
                }
                else {
                    resolve({
                        success: false,
                        message: (response === null || response === void 0 ? void 0 : response.getMessage()) || 'Buffer calculation failed',
                        bufferPolygons: []
                    });
                }
            });
        });
    }
};
